import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db } from '../firebase';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080810; color: #F5F5F0; font-family: 'Inter', sans-serif; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
  .pm-input:focus { outline: none; border-color: #F0B429 !important; box-shadow: 0 0 0 3px rgba(240,180,41,0.1); }
`;

const C = { gold: '#F0B429', goldDark: '#C9820A', bg: '#080810', card: '#14141f', border: 'rgba(255,255,255,0.08)', red: '#E63946' };

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.pitazomundial.app';
const isNativeApp = () => !!(window.Capacitor && window.Capacitor.isNativePlatform());
const isAndroidBrowser = () => /android/i.test(navigator.userAgent);

export default function LoginPage() {
  const { t } = useTranslation();
  const { linkId } = useParams();
  const { registerWithLink } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('loading');
  const [linkData, setLinkData] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  // appBannerDismissed no longer needed

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: (e.clientX / window.innerWidth) * 100, y: (e.clientY / window.innerHeight) * 100 });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    if (!linkId) { setStep('error'); return; }
    verifyLink();
  }, [linkId]);

  async function verifyLink() {
    try {
      const snap = await getDoc(doc(db, 'inviteLinks', linkId));
      if (!snap.exists()) { setStep('error'); return; }
      const data = snap.data();
      setLinkData(data);
      setName(data.participantName || '');
      const groupSnap = await getDoc(doc(db, 'groups', data.groupId));
      if (groupSnap.exists()) setGroupName(groupSnap.data().name || '');
      setStep('register');
    } catch(e) {
      setStep('error');
    }
  }

  async function handleRegister() {
    setError('');
    if (!name.trim()) { setError(t('your_name_label') + '.'); return; }
    if (!email.trim()) { setError(t('email_placeholder') + '.'); return; }
    if (password.length < 6) { setError(t('error_short_password')); return; }
    if (password !== confirmPassword) { setError(t('error_password_mismatch')); return; }
    setIsLoading(true);
    try {
      await registerWithLink(linkId, name, email, password);
      setStep('success');
    } catch(err) {
      setError(err.message || t('error_create_account'));
    }
    setIsLoading(false);
  }

  const inputStyle = {
    width: '100%', padding: '13px 16px',
    background: 'rgba(0,0,0,0.3)',
    border: `1px solid ${C.border}`,
    borderRadius: 12, color: '#F5F5F0',
    fontSize: 15, fontFamily: "'Inter',sans-serif",
    transition: 'all 0.2s ease'
  };

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: '#6B6B7B', marginBottom: 6,
    textTransform: 'uppercase', letterSpacing: 1
  };

  return (
    <div style={{ minHeight: '100vh', width: '100%', position: 'relative', overflow: 'hidden', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <style>{CSS}</style>

      <div style={{ position: 'absolute', inset: 0, opacity: 0.4, background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(240,180,41,0.3) 0%, rgba(201,130,10,0.15) 25%, transparent 70%)`, transition: 'background 0.3s ease' }} />
      <div style={{ position: 'absolute', top: '10%', left: '10%', width: 300, height: 300, borderRadius: '50%', background: 'rgba(240,180,41,0.15)', filter: 'blur(60px)', animation: 'float 6s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', bottom: '10%', right: '10%', width: 400, height: 400, borderRadius: '50%', background: 'rgba(201,130,10,0.1)', filter: 'blur(80px)', animation: 'float 8s ease-in-out infinite reverse' }} />

      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 420, animation: 'fadeIn 0.5s ease' }}>
        <div style={{ background: 'rgba(20,20,31,0.85)', backdropFilter: 'blur(20px)', border: `1px solid ${C.border}`, borderRadius: 24, padding: '36px 28px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>

          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ width: 72, height: 72, borderRadius: 18, background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 10px 30px rgba(240,180,41,0.3)' }}>
              <span style={{ fontSize: 36 }}>🏆</span>
            </div>
            <h1 style={{ fontFamily: "'Oswald',sans-serif", fontSize: 28, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
              PITAZO<span style={{ color: C.gold }}>MUNDIAL</span>
            </h1>
            {step === 'loading' && <p style={{ color: '#6B6B7B', fontSize: 13 }}>{t('login_verifying')}</p>}


          {step === 'register' && (
              <>
                <p style={{ color: '#6B6B7B', fontSize: 13, marginBottom: 4 }}>{t('login_invited_to')}</p>
                <p style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: C.gold, letterSpacing: 1 }}>🏟 {groupName}</p>
              </>
            )}
            {step === 'error' && <p style={{ color: C.red, fontSize: 13 }}>{t('login_invalid_link')}</p>}
          </div>

          {step === 'loading' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ width: 44, height: 44, border: `4px solid rgba(240,180,41,0.2)`, borderTop: `4px solid ${C.gold}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
            </div>
          )}

          {step === 'error' && (
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(230,57,70,0.08)', border: `1px solid rgba(230,57,70,0.2)`, borderRadius: 12 }}>
              <p style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{t('login_invalid_desc')}</p>
              <p style={{ color: '#6B6B7B', fontSize: 12 }}>{t('login_ask_admin')}</p>
              <button onClick={() => navigate('/')} style={{ marginTop: 16, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 20px', color: '#6B6B7B', fontFamily: "'Oswald',sans-serif", fontSize: 12, cursor: 'pointer' }}>
                {t('login_go_home')}
              </button>
            </div>
          )}

          {step === 'success' && (
            <SuccessScreen
              groupName={groupName}
              onContinue={() => { window.location.href = '/mis-grupos'; }}
            />
          )}

          {step === 'register' && (
            <div style={{ animation: 'fadeIn 0.4s ease' }}>

              <div style={{ background: 'rgba(240,180,41,0.06)', border: `1px solid rgba(240,180,41,0.15)`, borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontFamily: "'Inter',sans-serif", fontSize: 12, color: C.gold }}>
                {linkData?.used ? t('login_has_account') : t('login_new_account')}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{t('your_name_label')}</label>
                <input className="pm-input" value={name} onChange={e => setName(e.target.value)} placeholder={t('your_name_placeholder')} style={inputStyle} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Email</label>
                <input className="pm-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('email_placeholder')} style={inputStyle} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{t('password_placeholder')}</label>
                <div style={{ position: 'relative' }}>
                  <input className="pm-input" type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder={t('min_chars_placeholder')} style={{ ...inputStyle, paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowPass(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6B6B7B', cursor: 'pointer', fontSize: 18 }}>
                    {showPass ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>{t('confirm_password_label')}</label>
                <input className="pm-input" type={showPass ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder={t('repeat_password_placeholder')} style={inputStyle} onKeyDown={e => e.key === 'Enter' && handleRegister()} />
              </div>

              {error && (
                <div style={{ padding: '11px 14px', background: 'rgba(230,57,70,0.08)', border: `1px solid rgba(230,57,70,0.2)`, borderRadius: 10, color: C.red, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
                  {error}
                </div>
              )}

              <button onClick={handleRegister} disabled={isLoading}
                style={{ width: '100%', padding: '15px', background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, border: 'none', borderRadius: 12, color: C.bg, fontFamily: "'Oswald',sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', opacity: isLoading ? 0.7 : 1, boxShadow: '0 4px 15px rgba(240,180,41,0.3)' }}>
                {isLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <span style={{ width: 18, height: 18, border: '3px solid rgba(0,0,0,0.2)', borderTop: `3px solid ${C.bg}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    {t('login_entering')}
                  </span>
                ) : t('login_join_btn')}
              </button>

              <p style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: '#555', lineHeight: 1.6 }}>
                {t('login_hint')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PANTALLA DE ÉXITO CON DESCARGA ──────────────────────
function SuccessScreen({ groupName, onContinue }) {
  const isAndroid = /android/i.test(navigator.userAgent);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform());

  const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.pitazomundial.app';

  return (
    <div style={{ animation: 'fadeIn 0.5s ease', textAlign: 'center' }}>
      {/* Éxito */}
      <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
      <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, color: '#06B86A', letterSpacing: 1, marginBottom: 6 }}>
        ¡YA ESTÁS DENTRO!
      </div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6B6B7B', marginBottom: 24 }}>
        Te uniste a <span style={{ color: '#F0B429', fontWeight: 600 }}>{groupName}</span>
      </div>

      {/* Si ya está en la app nativa */}
      {isNative && (
        <button onClick={onContinue}
          style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg, #F0B429, #C9820A)', border: 'none', borderRadius: 12, color: '#080810', fontFamily: "'Oswald',sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', marginBottom: 12 }}>
          IR A MIS GRUPOS →
        </button>
      )}

      {/* Android browser → descargar app */}
      {!isNative && isAndroid && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, color: '#aaa', letterSpacing: 1, marginBottom: 12 }}>
            📱 DESCARGA LA APP PARA MEJOR EXPERIENCIA
          </div>
          <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '14px', background: 'linear-gradient(135deg, #34A853, #1a7a32)', border: 'none', borderRadius: 12, color: '#fff', fontFamily: "'Oswald',sans-serif", fontSize: 15, letterSpacing: 1, textDecoration: 'none', marginBottom: 10, boxShadow: '0 4px 15px rgba(52,168,83,0.3)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
              <path d="M3.18 23.76c.3.17.64.24.99.21l13.23-11.97L13.77 8.4 3.18 23.76z"/>
              <path d="M22.47 10.2L19.12 8.3l-3.72 3.7 3.72 3.7 3.38-1.93a1.93 1.93 0 000-3.57z"/>
              <path d="M4.17.24C3.82.21 3.48.28 3.18.45L16.9 12 20.53 8.4 4.17.24z"/>
              <path d="M3.18.45L3 .6v22.8l.18.15L17.4 12 3.18.45z" opacity=".5"/>
            </svg>
            DESCARGAR EN GOOGLE PLAY
          </a>
          <button onClick={onContinue}
            style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#6B6B7B', fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, cursor: 'pointer' }}>
            CONTINUAR EN WEB →
          </button>
        </div>
      )}

      {/* iOS → instrucciones */}
      {!isNative && isIOS && (
        <div style={{ background: 'rgba(0,122,255,0.08)', border: '1px solid rgba(0,122,255,0.2)', borderRadius: 14, padding: '16px', marginBottom: 16, textAlign: 'left' }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, color: '#60A5FA', letterSpacing: 1, marginBottom: 12, textAlign: 'center' }}>
            📲 AGREGAR A PANTALLA DE INICIO
          </div>
          {[
            '1. Toca el botón compartir (⬆️) en Safari',
            '2. Selecciona "Agregar a pantalla de inicio"',
            '3. Toca "Agregar" — funciona como una app',
          ].map((step, i) => (
            <div key={i} style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#aaa', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span>{step}</span>
            </div>
          ))}
          <button onClick={onContinue}
            style={{ width: '100%', padding: '12px', marginTop: 8, background: 'rgba(0,122,255,0.15)', border: '1px solid rgba(0,122,255,0.3)', borderRadius: 12, color: '#60A5FA', fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, cursor: 'pointer' }}>
            CONTINUAR EN WEB →
          </button>
        </div>
      )}

      {/* PC / otro */}
      {!isNative && !isAndroid && !isIOS && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#6B6B7B', marginBottom: 14 }}>
            Para mejor experiencia, abre este link desde tu celular Android y descarga la app.
          </div>
          <button onClick={onContinue}
            style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg, #F0B429, #C9820A)', border: 'none', borderRadius: 12, color: '#080810', fontFamily: "'Oswald',sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: 1, cursor: 'pointer' }}>
            IR A MIS GRUPOS →
          </button>
        </div>
      )}
    </div>
  );
}